import clsx from "clsx";
import { createElement, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

import { connectionPoints, fmt, frameOf, leavingAngle, lineStyleParts, primitivesOf, withMinStroke, type Frame, type SvgPart } from "./shapeSvg";

interface ShapeDrawingProps {
  /** The SymbolShape or LineStyle, as the symbol set gives it. */
  shape: unknown;
  kind: "symbol" | "line_style";
  /** What the drawing shows, for screen readers ("Symbol ball_valve_pid"). */
  label: string;
  /** The length of the longer side, in pixels. */
  size?: number;
  /** Draw the connection points: a ring for a guessed one, a dot for a confirmed one. */
  showPoints?: boolean;
  selected?: string | null;
  /** Given, the points can be chosen; with `onMovePoint` too, dragged (the move is sent when the point is let go). */
  onSelectPoint?: (name: string) => void;
  onMovePoint?: (name: string, x: number, y: number) => void;
  /** Draw the strokes at their true widths even when they come out thinner than a pixel (the large editor). */
  trueWidths?: boolean;
  className?: string;
}

/** The length of the line drawn to show a line style that has no drawn sample, in millimetres. */
const LINE_LENGTH_MM = 40;
/** How far the pointer must travel, in pixels, before pressing a point becomes dragging it (a click only selects). */
const DRAG_PX = 4;
/** The thinnest a stroke is drawn on a small drawing, in pixels. */
const MIN_STROKE_PX = 1;

function part(p: SvgPart, key: number) {
  return createElement(p.tag, { key, ...p.attrs }, p.text);
}

function withPoints(frame: Frame, xs: number[], ys: number[], pad: number): Frame {
  if (xs.length === 0) return frame;
  const x0 = Math.min(frame.x, ...xs.map((x) => x - pad));
  const y0 = Math.min(frame.y, ...ys.map((y) => y - pad));
  const x1 = Math.max(frame.x + frame.width, ...xs.map((x) => x + pad));
  const y1 = Math.max(frame.y + frame.height, ...ys.map((y) => y + pad));
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

interface Drag {
  name: string;
  /** where the pointer went down, in screen pixels */
  startX: number;
  startY: number;
  /** the point minus the pointer, in millimetres: the point keeps its place under the pointer, it does not jump */
  offsetX: number;
  offsetY: number;
  x: number;
  y: number;
  moved: boolean;
}

/**
 * A symbol or a line style drawn from its data (no picture file): millimetres in the viewBox, Y down, the first
 * primitive at the back. The drawing takes the colour of the text around it, so it reads in light and dark themes.
 */
export function ShapeDrawing({ shape, kind, label, size = 64, showPoints = false, selected = null, onSelectPoint, onMovePoint, trueWidths = false, className }: ShapeDrawingProps) {
  const svg = useRef<SVGSVGElement | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const style = kind === "line_style" ? (shape as { sample?: unknown } | null) : null;
  const geometry = kind === "line_style" ? style?.sample : shape;
  let frame: Frame;
  let parts: SvgPart[];
  if (kind === "line_style" && !geometry) {
    frame = { x: -1, y: -3, width: LINE_LENGTH_MM + 2, height: 6 };
    parts = lineStyleParts(shape, LINE_LENGTH_MM);
  } else {
    frame = frameOf(geometry);
    parts = primitivesOf(geometry);
  }
  const points = kind === "symbol" && showPoints ? connectionPoints(shape) : [];
  const marker = Math.max(frame.width, frame.height) * 0.035;
  frame = withPoints(frame, points.map((p) => p.x_mm), points.map((p) => p.y_mm), marker * 3);
  const wide = frame.width >= frame.height;
  const width = wide ? size : Math.max(8, Math.round((size * frame.width) / frame.height));
  const height = wide ? Math.max(8, Math.round((size * frame.height) / frame.width)) : size;
  const pxPerMm = width / frame.width;
  if (!trueWidths && pxPerMm > 0) parts = withMinStroke(parts, MIN_STROKE_PX / pxPerMm);

  const toMm = (event: ReactPointerEvent): { x: number; y: number } | null => {
    const matrix = svg.current?.getScreenCTM?.();
    if (!svg.current || !matrix) return null;
    const at = svg.current.createSVGPoint();
    at.x = event.clientX;
    at.y = event.clientY;
    const local = at.matrixTransform(matrix.inverse());
    return { x: local.x, y: local.y };
  };
  const round = (v: number) => Math.round(v * 100) / 100;

  return (
    <svg
      ref={svg}
      role="img"
      aria-label={label}
      className={clsx("shape-drawing", className)}
      width={width}
      height={height}
      viewBox={`${fmt(frame.x)} ${fmt(frame.y)} ${fmt(frame.width)} ${fmt(frame.height)}`}
      focusable="false"
    >
      <g className="shape-drawing__ink">{parts.map(part)}</g>
      {points.map((p) => {
        const at = drag && drag.name === p.name && drag.moved ? { x: drag.x, y: drag.y } : { x: p.x_mm, y: p.y_mm };
        const angle = (leavingAngle(p) * Math.PI) / 180;
        const interactive = !!onSelectPoint;
        return (
          <g
            key={p.name}
            className={clsx("shape-point", p.unsure && "shape-point--unsure", selected === p.name && "shape-point--selected")}
            role={interactive ? "button" : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-label={`Connection point ${p.name}${p.unsure ? ", guessed" : ""}`}
            onClick={interactive ? () => onSelectPoint?.(p.name) : undefined}
            onKeyDown={
              interactive
                ? (event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    onSelectPoint?.(p.name);
                  }
                : undefined
            }
            onPointerDown={
              onMovePoint
                ? (event) => {
                    if (event.button !== 0 || event.isPrimary === false) return; // only the main button or the first finger
                    (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
                    const mm = toMm(event);
                    setDrag({
                      name: p.name,
                      startX: event.clientX,
                      startY: event.clientY,
                      offsetX: mm ? p.x_mm - mm.x : 0,
                      offsetY: mm ? p.y_mm - mm.y : 0,
                      x: p.x_mm,
                      y: p.y_mm,
                      moved: false,
                    });
                  }
                : undefined
            }
            onPointerMove={
              onMovePoint
                ? (event) => {
                    if (!drag || drag.name !== p.name) return;
                    const far = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) >= DRAG_PX;
                    if (!drag.moved && !far) return; // a small jitter of the hand is still a click
                    const mm = toMm(event);
                    if (mm) setDrag({ ...drag, x: round(mm.x + drag.offsetX), y: round(mm.y + drag.offsetY), moved: true });
                  }
                : undefined
            }
            onPointerUp={
              onMovePoint
                ? () => {
                    if (drag && drag.name === p.name && drag.moved) onMovePoint(p.name, drag.x, drag.y);
                    setDrag(null);
                  }
                : undefined
            }
            onPointerCancel={onMovePoint ? () => setDrag(null) : undefined}
          >
            <line
              x1={fmt(at.x)}
              y1={fmt(at.y)}
              x2={fmt(at.x + Math.cos(angle) * marker * 2.5)}
              y2={fmt(at.y + Math.sin(angle) * marker * 2.5)}
              className="shape-point__lead"
              strokeWidth={fmt(marker / 2.5)}
            />
            <circle cx={fmt(at.x)} cy={fmt(at.y)} r={fmt(marker)} strokeWidth={fmt(marker / 2.5)} />
            <title>{p.name}</title>
          </g>
        );
      })}
    </svg>
  );
}
